import { useNavigate, useParams, Navigate, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Logo from "../components/Logo";
import LanguageSwitcher from "../components/LanguageSwitcher";
import Footer from "../components/Footer";
import { useLang } from "../context/LangContext";
import { LEGAL } from "../legal/content";

// Very small markdown renderer: **bold**, paragraphs, bullet-like "· ".
function renderBody(body) {
  return body.split(/\n\n+/).map((block, i) => {
    const lines = block.split("\n");
    return (
      <p key={i} className="text-brand-brown leading-relaxed mb-4 text-[15px]">
        {lines.map((line, j) => {
          const parts = line.split(/(\*\*[^*]+\*\*)/g);
          return (
            <span key={j} className="block">
              {parts.map((part, k) => {
                if (part.startsWith("**") && part.endsWith("**")) {
                  return (
                    <strong
                      key={k}
                      className="text-brand-deep-brown font-bold tracking-wide"
                    >
                      {part.slice(2, -2)}
                    </strong>
                  );
                }
                return <span key={k}>{part}</span>;
              })}
            </span>
          );
        })}
      </p>
    );
  });
}

export default function Legal() {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const navigate = useNavigate();

  const pack = LEGAL[lang] || LEGAL.es;
  const doc = pack.docs[slug];

  if (!doc) return <Navigate to="/legal/privacy" replace />;

  return (
    <div className="bg-brand-cream min-h-[100dvh]">
      <div className="border-b border-brand-tan/25 bg-brand-cream/85 backdrop-blur-xl sticky top-0 z-30">
        <div className="mx-auto max-w-3xl px-6 md:px-12 py-5 flex items-center justify-between gap-4">
          <button
            onClick={() => navigate(-1)}
            data-testid="legal-back"
            className="inline-flex items-center gap-2 text-brand-brown/70 text-xs tracking-[0.25em] uppercase font-bold hover:text-brand-brown"
          >
            <ArrowLeft size={14} strokeWidth={1.75} />
            ARISTEIA
          </button>
          <Logo size="sm" />
          <LanguageSwitcher />
        </div>
      </div>

      <article className="mx-auto max-w-3xl px-6 md:px-12 py-12 md:py-20">
        <p className="text-[10px] tracking-[0.45em] uppercase font-bold text-brand-tan">
          {pack.docs.privacy ? t("legal") || "Legal" : "Legal"}
        </p>
        <h1
          className="font-serif text-4xl md:text-6xl leading-[1.04] text-brand-brown mt-3"
          data-testid="legal-title"
        >
          {doc.title}
        </h1>
        <p className="mt-3 text-xs tracking-[0.3em] uppercase font-bold text-brand-tan">
          {pack.last_update}
        </p>

        <div className="mt-10 prose prose-brown max-w-none" data-testid="legal-body">
          {renderBody(doc.body)}
        </div>

        <div className="mt-16 flex flex-wrap gap-3 border-t border-brand-tan/30 pt-8">
          {Object.entries(pack.docs).map(([s, d]) =>
            s === slug ? null : (
              <Link
                key={s}
                to={`/legal/${s}`}
                data-testid={`legal-link-${s}`}
                className="px-4 py-2 rounded-full border border-brand-tan/40 text-xs tracking-[0.22em] uppercase font-bold text-brand-brown hover:bg-white transition-colors"
              >
                {d.title}
              </Link>
            ),
          )}
        </div>
      </article>

      <Footer />
    </div>
  );
}
